const NOTES = ['A', 'A#', 'Bb', 'B', 'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab'];

interface ResponseRecord {
  note: string;
  responseTime: number;
  isCorrect: boolean;
  timestamp: Date;
}

export function generateRandomNote(previousNote?: string): string {
  let availableNotes = [...NOTES];
  
  // Remove only the exact same note string from available options
  if (previousNote) {
    availableNotes = availableNotes.filter(note => note !== previousNote);
  }
  
  const randomIndex = Math.floor(Math.random() * availableNotes.length);
  return availableNotes[randomIndex];
}

export function generateAdaptiveNote(previousNote?: string, responseHistory?: ResponseRecord[]): string {
  let availableNotes = [...NOTES];
  
  // Remove only the exact same note string from available options
  if (previousNote) {
    availableNotes = availableNotes.filter(note => note !== previousNote);
  }
  
  // If no history, fall back to random selection
  if (!responseHistory || responseHistory.length === 0) {
    const randomIndex = Math.floor(Math.random() * availableNotes.length);
    return availableNotes[randomIndex];
  }
  
  // Calculate performance stats for each note
  const noteStats: { [note: string]: { 
    attempts: number; 
    correct: number; 
    avgResponseTime: number; 
    errorRate: number;
    weight: number;
  } } = {};
  
  // Initialize all available notes
  availableNotes.forEach(note => {
    noteStats[note] = {
      attempts: 0,
      correct: 0,
      avgResponseTime: 0,
      errorRate: 0,
      weight: 1.0 // Base weight for unplayed notes
    };
  });
  
  // Calculate stats from response history
  responseHistory.forEach(response => {
    const note = response.note;
    if (noteStats[note]) {
      noteStats[note].attempts += 1;
      if (response.isCorrect) {
        noteStats[note].correct += 1;
      }
    }
  });
  
  // Calculate error rates and average response times
  Object.keys(noteStats).forEach(note => {
    const stats = noteStats[note];
    if (stats.attempts > 0) {
      stats.errorRate = 1 - (stats.correct / stats.attempts);
      
      // Calculate average response time for correct answers only
      const correctResponses = responseHistory.filter(r => r.note === note && r.isCorrect);
      if (correctResponses.length > 0) {
        stats.avgResponseTime = correctResponses.reduce((sum, r) => sum + r.responseTime, 0) / correctResponses.length;
      }
    }
  });
  
  // Calculate weights based on performance
  const maxResponseTime = Math.max(...Object.values(noteStats).map(s => s.avgResponseTime).filter(t => t > 0));
  const normalizedMaxTime = maxResponseTime > 0 ? maxResponseTime : 3.0; // Fallback if no correct responses yet
  
  Object.keys(noteStats).forEach(note => {
    const stats = noteStats[note];
    
    if (stats.attempts === 0) {
      // Unplayed notes get moderate weight (should be learned)
      stats.weight = 2.0;
    } else {
      // Weight based on error rate (most important factor)
      const errorWeight = 1.0 + (stats.errorRate * 4.0); // 1.0 to 5.0 range
      
      // Weight based on response time (secondary factor)
      const timeWeight = stats.avgResponseTime > 0 ? 1.0 + (stats.avgResponseTime / normalizedMaxTime) : 1.0; // 1.0 to 2.0 range
      
      // Combine weights with error rate being more important
      stats.weight = errorWeight * 0.7 + timeWeight * 0.3;
      
      // Boost weight for notes with very few attempts (need more data)
      if (stats.attempts < 3) {
        stats.weight *= 1.5;
      }
      
      // Reduce weight slightly for notes with high accuracy and fast response (but don't eliminate them)
      if (stats.errorRate < 0.2 && stats.avgResponseTime < normalizedMaxTime * 0.5) {
        stats.weight *= 0.4; // Still practice successful notes, but less frequently
      }
    }
  });
  
  // Create weighted selection array
  const weightedNotes: string[] = [];
  availableNotes.forEach(note => {
    const weight = Math.max(0.1, noteStats[note].weight); // Minimum weight ensures all notes can be selected
    const count = Math.ceil(weight * 10); // Scale weights to reasonable integers
    for (let i = 0; i < count; i++) {
      weightedNotes.push(note);
    }
  });
  
  // Select randomly from weighted array
  const randomIndex = Math.floor(Math.random() * weightedNotes.length);
  return weightedNotes[randomIndex];
}

export function getAllNotes(): string[] {
  return [...NOTES];
}

export function isValidNote(note: string): boolean {
  return NOTES.includes(note);
}

// Convert any note to its sharp equivalent for pitch detection comparison
export function normalizeNoteForDetection(note: string): string {
  const flatToSharp: { [key: string]: string } = {
    'Bb': 'A#',
    'Db': 'C#',
    'Eb': 'D#',
    'Gb': 'F#',
    'Ab': 'G#'
  };
  
  return flatToSharp[note] || note;
}

// For display purposes, show the exact note as requested (no conversion)
export function getNoteDisplayName(note: string): string {
  // Convert flat notation to proper flat symbol
  const flatNoteDisplay: { [key: string]: string } = {
    'Bb': 'B♭',
    'Db': 'D♭',
    'Eb': 'E♭',
    'Gb': 'G♭',
    'Ab': 'A♭'
  };
  
  return flatNoteDisplay[note] || note;
}

// Triad types
export type TriadType = 'major' | 'minor';

export interface Triad {
  root: string;
  type: TriadType;
  notes: [string, string, string]; // [root, third, fifth]
}

// Generate a random triad
export function generateRandomTriad(): Triad {
  const rootNote = generateRandomNote();
  const type: TriadType = Math.random() < 0.5 ? 'major' : 'minor';
  const notes = getTriadNotes(rootNote, type);
  
  
  return {
    root: rootNote,
    type,
    notes
  };
}

// Get the three notes of a triad given root and type
export function getTriadNotes(root: string, type: TriadType): [string, string, string] {
  // Define chromatic scale starting from C for interval calculations
  const chromaticScale = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  
  // Normalize the root note to sharp notation for calculation only
  const normalizedRoot = normalizeNoteForDetection(root);
  const rootIndex = chromaticScale.indexOf(normalizedRoot);
  
  
  if (rootIndex === -1) {
    throw new Error(`Invalid root note: ${root}`);
  }
  
  // Calculate intervals (semitones from root)
  const thirdInterval = type === 'major' ? 4 : 3; // Major 3rd = 4 semitones, Minor 3rd = 3 semitones
  const fifthInterval = 7; // Perfect 5th = 7 semitones for both major and minor
  
  // Calculate note indices with wrap-around
  const thirdIndex = (rootIndex + thirdInterval) % 12;
  const fifthIndex = (rootIndex + fifthInterval) % 12;
  
  // Get base notes from chromatic scale
  const thirdSharp = chromaticScale[thirdIndex];
  const fifthSharp = chromaticScale[fifthIndex];
  
  // Determine if we should use flats based on the root note
  const useFlats = root.includes('b');
  
  // Convert to appropriate notation
  const third = useFlats ? convertToFlatIfApplicable(thirdSharp) : thirdSharp;
  const fifth = useFlats ? convertToFlatIfApplicable(fifthSharp) : fifthSharp;
  
  return [root, third, fifth]; // Use original root
}

// Helper function to convert sharp notes to flat equivalents where applicable
function convertToFlatIfApplicable(note: string): string {
  const sharpToFlat: { [key: string]: string } = {
    'C#': 'Db',
    'D#': 'Eb', 
    'F#': 'Gb',
    'G#': 'Ab',
    'A#': 'Bb'
  };
  
  return sharpToFlat[note] || note;
}